import React, { useRef } from 'react';
import { CSVLink } from 'react-csv';
import GenericModal from '~/components/GenericModal/GenericModal';
import useBuildingBulkImport from '~/hooks/building/useBuildingBulkImport';
import { BulkImportStatusRow } from '~/api/building';
import classes from './BuildingBulkImport.module.css';

const TEMPLATE_HEADERS = [
  'Building Name',
  'Time Zone',
  'Website URL',
  'Building Image',
  'Building Logo',
  'Street',
  'City',
  'State',
  'ZIP',
  'Resident Portal URL',
  'PMS Software',
  'Property Type',
  'Owner',
  'PM'
];

const PREVIEW_STATUS_LABEL = {
  create: '✅ Will create',
  finish: '🔧 Half made, will finish',
  skip: '⏭ Exists, will skip',
  invalid: '❌ Invalid'
};

const REVIEW_LABEL: Record<string, string> = {
  matched: 'Found',
  needsReview: 'Needs check',
  notFound: 'Not found',
  skipped: 'Already set',
  error: 'Lookup failed'
};

const rowOutcome = (row: BulkImportStatusRow) => {
  if (row.status === 'failed') {
    return row.result?.outcome === 'partial'
      ? `❌ Failed after the building was created (upload the CSV again to finish it): ${row.error}`
      : `❌ Failed: ${row.error}`;
  }
  if (row.status === 'stuck') return '⚠️ Stuck — check this building';
  if (row.status !== 'completed') return '⏳ Waiting';
  if (row.result?.outcome === 'skipped') return '⏭ Skipped (name exists)';
  return row.result?.joinLink === 'failed'
    ? '✅ Created (join link failed)'
    : '✅ Created';
};

const toResultsCsv = (rows: BulkImportStatusRow[]) =>
  rows.map((row) => ({
    sheetRow: row.rowNumber,
    building: row.buildingName,
    outcome: rowOutcome(row),
    buildingUuid: row.result?.buildingUuid || '',
    reviewStatus: row.result?.reviewStatus
      ? REVIEW_LABEL[row.result.reviewStatus]
      : '',
    reviewUrl: row.result?.reviewUrl || '',
    flyerLogo: row.result?.flyerLogo || ''
  }));

interface IProps {
  onClose: () => void;
}

const BuildingBulkImport = ({ onClose }: IProps) => {
  const {
    file,
    setFile,
    preview,
    validate,
    isValidating,
    startImport,
    isStarting,
    canImport,
    status,
    lastImport
  } = useBuildingBulkImport();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // No file changes while validating, so a late Validate result can't land on another file.
  const fileLocked = !!status?.active || isValidating;

  const processed = status ? status.completed + status.failed : 0;
  const percent =
    status && status.total > 0
      ? Math.round((processed / status.total) * 100)
      : 0;

  const renderPreview = () =>
    preview && (
      <div className={classes.section}>
        <div className={classes.stats}>
          <span>Total: {preview.stats.total}</span>
          <span>Will create: {preview.stats.toCreate}</span>
          <span>Will finish: {preview.stats.toFinish}</span>
          <span>Will skip: {preview.stats.toSkip}</span>
          <span>Invalid: {preview.stats.invalid}</span>
        </div>
        {preview.missingColumns.length > 0 && (
          <span className={classes.error}>
            Missing columns: {preview.missingColumns.join(', ')}
          </span>
        )}
        {!preview.canImport && (
          <span className={classes.error}>
            Fix the CSV and validate again. Import needs no invalid rows and at
            least one building to create or finish.
          </span>
        )}
        <div className={classes.tableWrap}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>Sheet row</th>
                <th>Building</th>
                <th>Status</th>
                <th>Problems</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row) => (
                <tr key={row.rowNumber}>
                  <td>{row.rowNumber}</td>
                  <td>{row.name || '—'}</td>
                  <td>{PREVIEW_STATUS_LABEL[row.status]}</td>
                  <td>{row.errors.join('; ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

  const renderStatus = () =>
    status && (
      <div className={classes.section}>
        <div className={classes.row}>
          <span className={classes.stats}>
            {status.active ? 'Importing' : 'Finished'}: {processed} of{' '}
            {status.total} done
            {status.failed > 0 && `, ${status.failed} failed`}
          </span>
          <span className={classes.stats}>{percent}%</span>
        </div>
        <div className={classes.progressTrack}>
          <div
            className={classes.progressFill}
            style={{ width: `${percent}%` }}
          />
        </div>
        <span className={classes.hint}>
          {status.active
            ? 'This runs in the background — you can close this and come back.'
            : status.abandoned
            ? 'This import stopped before finishing. Upload the same CSV again — done buildings are skipped, half-made ones are finished.'
            : ''}
        </span>
        {status.rows.length > 0 && (
          <>
            <div className={classes.tableWrap}>
              <table className={classes.table}>
                <thead>
                  <tr>
                    <th>Sheet row</th>
                    <th>Building</th>
                    <th>Result</th>
                    <th>Review link</th>
                    <th>Flyer logo</th>
                  </tr>
                </thead>
                <tbody>
                  {status.rows.map((row) => (
                    <tr key={row.rowNumber}>
                      <td>{row.rowNumber}</td>
                      <td>
                        {row.result?.buildingUuid ? (
                          <a
                            href={`/building/${row.result.buildingUuid}`}
                            target='_blank'
                            rel='noreferrer'
                          >
                            {row.buildingName}
                          </a>
                        ) : (
                          row.buildingName
                        )}
                      </td>
                      <td>{rowOutcome(row)}</td>
                      <td>
                        {row.result?.reviewUrl ? (
                          <a
                            href={row.result.reviewUrl}
                            target='_blank'
                            rel='noreferrer'
                          >
                            {REVIEW_LABEL[row.result.reviewStatus || 'matched']}
                          </a>
                        ) : (
                          (row.result?.reviewStatus &&
                            REVIEW_LABEL[row.result.reviewStatus]) ||
                          ''
                        )}
                      </td>
                      <td>
                        {row.result?.flyerLogo === 'skipped'
                          ? 'Skipped (logo not transparent)'
                          : row.result?.flyerLogo === 'used'
                          ? 'Same as leads logo'
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!status.active && (
              <div className={classes.row}>
                <CSVLink
                  data={toResultsCsv(status.rows)}
                  filename='building-bulk-import-results.csv'
                  className={classes.link}
                >
                  Download results CSV
                </CSVLink>
              </div>
            )}
          </>
        )}
      </div>
    );

  return (
    <GenericModal
      showModal
      title='Import Buildings from CSV'
      width='960px'
      enableOverflowScrolls
      dismissOnTapOutside={false}
      cancelOverride='Close'
      onChange={onClose}
      actionText={isStarting ? 'Starting…' : 'Import'}
      actionFunction={startImport}
      actionButtonDisabled={!canImport}
      content={
        <div>
          <p className={classes.intro}>
            One building per row. Drive links must be shared as “anyone with the
            link”. Short Name is set to the Building Name.
          </p>
          {/* Hidden: a global input[type="file"] rule hides it anyway; the drop zone opens it. */}
          <input
            ref={fileInputRef}
            type='file'
            accept='.csv,text/csv'
            style={{ display: 'none' }}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              // Lets the same (edited) file fire onChange when picked again.
              e.target.value = '';
            }}
          />
          <div className={classes.uploadRow}>
            <div
              role='button'
              tabIndex={0}
              className={`${classes.dropZone} ${
                fileLocked ? classes.dropZoneDisabled : ''
              }`}
              onClick={() => !fileLocked && fileInputRef.current?.click()}
              onKeyDown={(e) =>
                e.key === 'Enter' &&
                !fileLocked &&
                fileInputRef.current?.click()
              }
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (!fileLocked && e.dataTransfer.files?.[0]) {
                  setFile(e.dataTransfer.files[0]);
                }
              }}
            >
              {file ? (
                <>
                  <span className={classes.fileName}>📄 {file.name}</span>
                  <span className={classes.hint}>Click to choose another</span>
                </>
              ) : (
                <>
                  <span className={classes.fileName}>
                    Drop your CSV here or click to choose
                  </span>
                  <span className={classes.hint}>
                    Then press Validate. Import unlocks once every row passes.
                  </span>
                </>
              )}
            </div>
            <button
              type='button'
              className='btn-standard btn-primary'
              disabled={!file || isValidating || !!status?.active}
              onClick={validate}
            >
              {isValidating ? 'Validating…' : 'Validate'}
            </button>
          </div>
          <div className={classes.links}>
            <CSVLink
              data={[TEMPLATE_HEADERS]}
              filename='building-bulk-import-template.csv'
              className={classes.link}
            >
              Download template
            </CSVLink>
            {/* Shown results already have their own download. */}
            {!status && !!lastImport?.rows.length && (
              <CSVLink
                data={toResultsCsv(lastImport.rows)}
                filename='building-bulk-import-last-results.csv'
                className={classes.link}
              >
                Download last import results
              </CSVLink>
            )}
          </div>
          {status?.active && (
            <p className={classes.hint}>
              An import is running. Wait for it to finish before starting
              another.
            </p>
          )}
          {renderPreview()}
          {renderStatus()}
        </div>
      }
    />
  );
};

export default BuildingBulkImport;
